/** @typedef {[number, number]} Point */
/** @typedef {import('./floorPlanDesignerState.js').DesignerState} DesignerState */

import {
  EXTERIOR_FACE_CANVAS_AREA_RATIO,
  MIN_ROOM_AREA_M2,
  ROOM_DETECT_DEBUG,
} from './floorPlanDesignerConfig.js';
import { isPolygonSelfIntersecting } from './floorPlanDesignerWallRectangles.js';
import { getPointById, getWallSegmentsResolved } from './floorPlanDesignerState.js';

/**
 * @param {Point} a
 * @param {Point} b
 * @returns {number}
 */
export function distance(a, b) {
  return Math.hypot(b[0] - a[0], b[1] - a[1]);
}

/**
 * @param {Point} start
 * @param {Point} end
 * @returns {number}
 */
export function segmentLength(start, end) {
  return distance(start, end);
}

/**
 * @param {Point} wallStart
 * @param {Point} wallEnd
 * @param {Point} point
 * @returns {{ dist: number; t: number; projected: Point }}
 */
export function projectPointOntoSegment(wallStart, wallEnd, point) {
  const dx = wallEnd[0] - wallStart[0];
  const dy = wallEnd[1] - wallStart[1];
  const lenSq = dx * dx + dy * dy;
  if (lenSq < 1e-6) {
    return { dist: distance(wallStart, point), t: 0, projected: [...wallStart] };
  }
  let t = ((point[0] - wallStart[0]) * dx + (point[1] - wallStart[1]) * dy) / lenSq;
  t = Math.max(0, Math.min(1, t));
  const projected = [wallStart[0] + t * dx, wallStart[1] + t * dy];
  return { dist: distance(projected, point), t, projected };
}

/**
 * @param {DesignerState} state
 * @param {Point} point
 * @param {number} [threshold]
 * @returns {{ wallId: string; start: Point; end: Point; centerRatio: number; projected: Point } | null}
 */
export function findNearestWall(state, point, threshold = 20) {
  const segments = getWallSegmentsResolved(state);
  let best = null;
  let bestDist = Infinity;

  for (const seg of segments) {
    const { dist, t, projected } = projectPointOntoSegment(seg.start, seg.end, point);
    if (dist < bestDist) {
      bestDist = dist;
      best = {
        wallId: seg.id,
        start: seg.start,
        end: seg.end,
        centerRatio: t,
        projected,
      };
    }
  }

  if (!best || bestDist > threshold) return null;
  return best;
}

/**
 * @param {Point} start
 * @param {Point} end
 * @returns {Point}
 */
export function segmentMidpoint(start, end) {
  return [(start[0] + end[0]) / 2, (start[1] + end[1]) / 2];
}

/**
 * @param {Point} start
 * @param {Point} end
 * @returns {number}
 */
export function segmentAngleDeg(start, end) {
  return (Math.atan2(end[1] - start[1], end[0] - start[0]) * 180) / Math.PI;
}

/**
 * Signed polygon area in image pixels (Y-down canvas).
 * Positive = clockwise traversal; negative = counter-clockwise.
 * @param {Array<Point>} polygon
 * @returns {number}
 */
export function signedPolygonArea2D(polygon) {
  if (polygon.length < 3) return 0;
  let sum = 0;
  for (let i = 0; i < polygon.length; i += 1) {
    const j = (i + 1) % polygon.length;
    sum += polygon[i][0] * polygon[j][1] - polygon[j][0] * polygon[i][1];
  }
  return sum / 2;
}

/**
 * @param {Array<Point>} polygon
 * @returns {number}
 */
export function polygonAreaSqM(polygon, metersPerPixel) {
  if (polygon.length < 3) return 0;
  let sum = 0;
  for (let i = 0; i < polygon.length; i += 1) {
    const j = (i + 1) % polygon.length;
    sum += polygon[i][0] * polygon[j][1] - polygon[j][0] * polygon[i][1];
  }
  const areaPx = Math.abs(sum) / 2;
  return areaPx * metersPerPixel * metersPerPixel;
}

/**
 * @param {Array<Point>} polygon
 * @returns {Point}
 */
export function polygonCentroid(polygon) {
  if (!polygon.length) return [0, 0];
  let cx = 0;
  let cy = 0;
  for (const pt of polygon) {
    cx += pt[0];
    cy += pt[1];
  }
  return [cx / polygon.length, cy / polygon.length];
}

/**
 * @param {DesignerState} state
 * @param {string} fromId
 * @param {string} toId
 * @returns {number}
 */
function edgeAngleRad(state, fromId, toId) {
  const from = getPointById(state, fromId);
  const to = getPointById(state, toId);
  if (!from || !to) return 0;
  return Math.atan2(to.y - from.y, to.x - from.x);
}

/**
 * @typedef {{
 *   pointIds: string[];
 *   polygon: Point[];
 *   areaSqM: number;
 *   wallIds: string[];
 *   boundaryEdges: Array<{ wallId: string; fromPointId: string; toPointId: string }>;
 * }} DetectedFace
 */

/**
 * @typedef {{
 *   outgoing: Map<string, Array<{ to: string; wallId: string; angle: number }>>;
 *   halfEdgeKey: (fromId: string, toId: string) => string;
 *   nextHalfEdge: (fromId: string, toId: string) => { from: string; to: string; wallId: string } | null;
 * }} DirectedEdgeGraph
 */

/**
 * Build directed half-edge adjacency from physical walls (topology via point IDs).
 * @param {DesignerState} state
 * @returns {DirectedEdgeGraph | null}
 */
export function buildDirectedEdgeGraph(state) {
  const { walls } = state;
  if (walls.length < 3) return null;

  /** @type {Map<string, Array<{ to: string; wallId: string; angle: number }>>} */
  const outgoing = new Map();

  for (const wall of walls) {
    const a = wall.startPointId;
    const b = wall.endPointId;
    if (a === b) continue;
    const angleAB = edgeAngleRad(state, a, b);
    const angleBA = edgeAngleRad(state, b, a);
    if (!outgoing.has(a)) outgoing.set(a, []);
    if (!outgoing.has(b)) outgoing.set(b, []);
    outgoing.get(a).push({ to: b, wallId: wall.id, angle: angleAB });
    outgoing.get(b).push({ to: a, wallId: wall.id, angle: angleBA });
  }

  for (const edges of outgoing.values()) {
    edges.sort((left, right) => left.angle - right.angle);
  }

  const halfEdgeKey = (fromId, toId) => `${fromId}->${toId}`;

  /**
   * Face walk: at vertex `toId`, incoming from `fromId`, take next edge counterclockwise
   * in standard math coords. Canvas is Y-down (see signedPolygonArea2D).
   * @param {string} fromId
   * @param {string} toId
   */
  const nextHalfEdge = (fromId, toId) => {
    const list = outgoing.get(toId) || [];
    const reverseIdx = list.findIndex((edge) => edge.to === fromId);
    if (reverseIdx < 0) return null;
    const nextIdx = (reverseIdx - 1 + list.length) % list.length;
    const next = list[nextIdx];
    return { from: toId, to: next.to, wallId: next.wallId };
  };

  return { outgoing, halfEdgeKey, nextHalfEdge };
}

/**
 * Enumerate closed face cycles via half-edge traversal.
 * @param {DesignerState} state
 * @param {DirectedEdgeGraph} graph
 * @returns {DetectedFace[]}
 */
export function enumeratePlanarFaces(state, graph) {
  const { walls, scale } = state;
  const { outgoing, halfEdgeKey, nextHalfEdge } = graph;

  /** @type {Set<string>} */
  const usedHalfEdges = new Set();
  /** @type {DetectedFace[]} */
  const rawFaces = [];

  for (const wall of walls) {
    for (const [startFrom, startTo] of [
      [wall.startPointId, wall.endPointId],
      [wall.endPointId, wall.startPointId],
    ]) {
      const startKey = halfEdgeKey(startFrom, startTo);
      if (usedHalfEdges.has(startKey)) continue;

      const pointIds = [startFrom];
      const wallIds = [];
      let currentFrom = startFrom;
      let currentTo = startTo;
      let guard = 0;
      let closed = false;

      while (guard < walls.length * 4 + 10) {
        guard += 1;
        const key = halfEdgeKey(currentFrom, currentTo);
        if (usedHalfEdges.has(key)) break;

        const outList = outgoing.get(currentFrom) || [];
        const targetTo = currentTo;
        const edge = outList.find((item) => item.to === targetTo);
        if (!edge) break;

        usedHalfEdges.add(key);
        wallIds.push(edge.wallId);
        pointIds.push(currentTo);

        if (currentTo === startFrom && pointIds.length >= 4) {
          closed = true;
          break;
        }

        const next = nextHalfEdge(currentFrom, currentTo);
        if (!next) break;
        currentFrom = next.from;
        currentTo = next.to;
      }

      if (!closed || pointIds.length < 4) continue;

      const loopPointIds = pointIds.slice(0, -1);
      /** @type {DetectedFace['boundaryEdges']} */
      const boundaryEdges = [];
      for (let i = 0; i < loopPointIds.length; i += 1) {
        const fromPointId = loopPointIds[i];
        const toPointId = loopPointIds[(i + 1) % loopPointIds.length];
        const outList = outgoing.get(fromPointId) || [];
        const directed = outList.find((item) => item.to === toPointId);
        if (directed) {
          boundaryEdges.push({
            wallId: directed.wallId,
            fromPointId,
            toPointId,
          });
        }
      }

      const polygon = loopPointIds.map((pid) => {
        const pt = getPointById(state, pid);
        return pt ? [pt.x, pt.y] : [0, 0];
      });

      rawFaces.push({
        pointIds: loopPointIds,
        polygon,
        areaSqM: polygonAreaSqM(polygon, scale.metersPerPixel),
        wallIds: [...new Set(wallIds)],
        boundaryEdges,
      });
    }
  }

  return rawFaces;
}

/**
 * @param {DetectedFace} face
 * @param {string} reason
 * @param {boolean} selfIntersecting
 */
function logRejectedCycle(face, reason, selfIntersecting) {
  if (!ROOM_DETECT_DEBUG) return;
  console.debug('[Room Detection] rejected cycle', {
    reason,
    areaM2: face.areaSqM,
    selfIntersecting,
    pointIds: face.pointIds,
    wallIds: face.wallIds,
  });
}

/**
 * @param {{
 *   canvasAreaSqM: number;
 * }} ctx
 * @param {DetectedFace[]} rawFaces
 * @returns {{ rooms: DetectedFace[]; rejected: Array<{ face: DetectedFace; reason: string }> }}
 */
export function filterDetectedRoomFaces(rawFaces, ctx) {
  /** @type {Set<string>} */
  const seenWallSets = new Set();
  /** @type {DetectedFace[]} */
  const candidates = [];
  /** @type {Array<{ face: DetectedFace; reason: string }>} */
  const rejected = [];

  for (const face of rawFaces) {
    if (face.areaSqM < MIN_ROOM_AREA_M2) {
      rejected.push({ face, reason: 'area_below_min' });
      logRejectedCycle(face, 'area_below_min', isPolygonSelfIntersecting(face.polygon));
      continue;
    }

    const wallSetKey = [...face.wallIds].sort().join('|');
    if (seenWallSets.has(wallSetKey)) {
      rejected.push({ face, reason: 'duplicate_wall_set' });
      logRejectedCycle(face, 'duplicate_wall_set', isPolygonSelfIntersecting(face.polygon));
      continue;
    }
    seenWallSets.add(wallSetKey);

    if (isPolygonSelfIntersecting(face.polygon)) {
      rejected.push({ face, reason: 'self_intersecting' });
      logRejectedCycle(face, 'self_intersecting', true);
      continue;
    }

    candidates.push(face);
  }

  if (candidates.length === 0) {
    return { rooms: [], rejected };
  }

  /** Drop wrap-around faces that contain two+ smaller room centroids (partition false cycle). */
  const withoutWrapFaces = candidates.filter((face) => {
    let smallerCentroidsInside = 0;
    for (const other of candidates) {
      if (other === face || other.areaSqM >= face.areaSqM * 0.99) continue;
      if (isPointInPolygon(polygonCentroid(other.polygon), face.polygon)) {
        smallerCentroidsInside += 1;
      }
    }
    if (smallerCentroidsInside >= 2) {
      rejected.push({ face, reason: 'wrap_face_multiple_rooms' });
      logRejectedCycle(face, 'wrap_face_multiple_rooms', isPolygonSelfIntersecting(face.polygon));
      return false;
    }
    return true;
  });

  let exteriorIdx = -1;
  let maxArea = -1;
  for (let i = 0; i < withoutWrapFaces.length; i += 1) {
    const face = withoutWrapFaces[i];
    if (
      Number.isFinite(ctx.canvasAreaSqM) &&
      face.areaSqM >= ctx.canvasAreaSqM * EXTERIOR_FACE_CANVAS_AREA_RATIO &&
      face.areaSqM > maxArea
    ) {
      maxArea = face.areaSqM;
      exteriorIdx = i;
    }
  }

  /** @type {DetectedFace[]} */
  const rooms = [];
  for (let i = 0; i < withoutWrapFaces.length; i += 1) {
    const face = withoutWrapFaces[i];
    if (i === exteriorIdx) {
      rejected.push({ face, reason: 'exterior_face' });
      logRejectedCycle(face, 'exterior_face', false);
      continue;
    }
    rooms.push(face);
  }

  return { rooms, rejected };
}

/**
 * @param {DesignerState} state
 * @returns {DetectedFace[]}
 */
export function findClosedPolygons(state) {
  const graph = buildDirectedEdgeGraph(state);
  if (!graph) return [];

  const rawFaces = enumeratePlanarFaces(state, graph);
  const canvasAreaSqM =
    state.canvasWidth > 0 && state.canvasHeight > 0
      ? state.canvasWidth *
        state.canvasHeight *
        state.scale.metersPerPixel *
        state.scale.metersPerPixel
      : Infinity;

  const { rooms } = filterDetectedRoomFaces(rawFaces, { canvasAreaSqM });
  return rooms;
}

/**
 * @param {string} wallId
 * @param {string} fromPointId
 * @param {string} toPointId
 * @returns {string}
 */
export function directedEdgeKey(wallId, fromPointId, toPointId) {
  return `${wallId}:${fromPointId}->${toPointId}`;
}

/**
 * Walls shared by two or more detected rooms (partition walls).
 * @param {Array<{ wallIds: string[] }>} rooms
 * @returns {Set<string>}
 */
export function getPartitionWallIds(rooms) {
  /** @type {Map<string, number>} */
  const roomCountByWall = new Map();
  for (const room of rooms) {
    for (const wallId of room.wallIds) {
      roomCountByWall.set(wallId, (roomCountByWall.get(wallId) || 0) + 1);
    }
  }
  return new Set(
    [...roomCountByWall.entries()].filter(([, count]) => count >= 2).map(([wallId]) => wallId),
  );
}

/**
 * @param {Point} point
 * @param {Array<Point>} polygon
 * @returns {boolean}
 */
export function isPointInPolygon(point, polygon) {
  if (polygon.length < 3) return false;
  const [px, py] = point;
  let inside = false;
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const xi = polygon[i][0];
    const yi = polygon[i][1];
    const xj = polygon[j][0];
    const yj = polygon[j][1];
    const intersect = (yi > py) !== (yj > py) && px < ((xj - xi) * (py - yi)) / (yj - yi) + xi;
    if (intersect) inside = !inside;
  }
  return inside;
}

/**
 * @param {Array<{ polygon: Point[]; wallIds: string[]; areaSqM: number; pointIds: string[] }>} rooms
 * @param {Point} point
 * @returns {{ room: { polygon: Point[]; wallIds: string[]; areaSqM: number; pointIds: string[] }; roomIndex: number } | null}
 */
export function findRoomContainingPoint(rooms, point) {
  for (let i = 0; i < rooms.length; i += 1) {
    if (isPointInPolygon(point, rooms[i].polygon)) {
      return { room: rooms[i], roomIndex: i };
    }
  }
  return null;
}

/**
 * @param {Array<Point>} polygon
 * @param {number} metersPerPixel
 * @returns {{ widthM: number; depthM: number }}
 */
export function roomAxisAlignedBoundsM(polygon, metersPerPixel) {
  if (polygon.length < 2) {
    return { widthM: 0, depthM: 0 };
  }
  const xs = polygon.map((p) => p[0]);
  const ys = polygon.map((p) => p[1]);
  const widthM = (Math.max(...xs) - Math.min(...xs)) * metersPerPixel;
  const depthM = (Math.max(...ys) - Math.min(...ys)) * metersPerPixel;
  return { widthM, depthM };
}

export { openingSpanOnWall as openingEndpointsFromCenterRatio } from './floorPlanWallGeometry.js';
export { buildDetectedRooms, buildRoomBoundaryModel } from './floorPlanDesignerRoomBoundaries.js';

/**
 * @param {Point} pointer
 * @param {number} canvasWidth
 * @param {number} canvasHeight
 * @returns {Point}
 */
export function clampToCanvas(pointer, canvasWidth, canvasHeight) {
  return [
    Math.max(0, Math.min(canvasWidth, pointer[0])),
    Math.max(0, Math.min(canvasHeight, pointer[1])),
  ];
}

/**
 * @param {number} stageWidth
 * @param {number} stageHeight
 * @param {{ offsetX: number; offsetY: number; displayScale: number; panX?: number; panY?: number }} transform
 * @param {number} [marginM]
 * @param {number} [pixelsPerMeter]
 * @returns {{ minX: number; maxX: number; minY: number; maxY: number }}
 */
export function getVisibleBounds(
  stageWidth,
  stageHeight,
  transform,
  marginM = 2,
  pixelsPerMeter = 100,
) {
  const marginPx = marginM * pixelsPerMeter;
  const corners = [
    [0, 0],
    [stageWidth, 0],
    [stageWidth, stageHeight],
    [0, stageHeight],
  ].map(([sx, sy]) => {
    const panX = transform.panX || 0;
    const panY = transform.panY || 0;
    const scale = transform.displayScale || 1;
    return [
      (sx - transform.offsetX - panX) / scale,
      (sy - transform.offsetY - panY) / scale,
    ];
  });

  const xs = corners.map((c) => c[0]);
  const ys = corners.map((c) => c[1]);
  return {
    minX: Math.min(...xs) - marginPx,
    maxX: Math.max(...xs) + marginPx,
    minY: Math.min(...ys) - marginPx,
    maxY: Math.max(...ys) + marginPx,
  };
}
